import { type ChangeEvent, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { StarScale2 as StarScaleIcon } from '@elfsight/icons';
import { getColorWithAlpha, isDarkColor } from '@elfsight/lib-utils';
import { Icon } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import { commonFieldsSettings } from '../commonFieldSettings';
import type { CommonFieldProps } from '../types';

const STAR_SIZE = 40;

export interface StarScaleProps extends CommonFieldProps<number> {
  numStars: number;
}

export function StarScale({
  id,
  value,
  label,
  numStars,
  onChange,
  onFocus,
  required,
  disabled = false,
  fieldSpacing = commonFieldsSettings.fieldSpacing,
  fieldColor = commonFieldsSettings.fieldColor,
  fieldActiveColor = commonFieldsSettings.fieldActiveColor
}: StarScaleProps) {
  const checkedIndex = value !== undefined ? value : 0;

  const handleCheck = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      onChange?.(parseInt(value), event);
    },
    [onChange]
  );

  return (
    <StarsContainer
      role="radiogroup"
      aria-required={required}
      _gap={fieldSpacing}
    >
      {Array(numStars)
        .fill(null)
        .map((_, index) => {
          const derivedIndex = index + 1;
          const isFilled = derivedIndex <= checkedIndex;
          const delay = index * 0.05;

          return (
            <IconContainer
              key={derivedIndex}
              _isFilled={isFilled}
              _disabled={disabled}
            >
              {index}

              <Input
                id={`${id}-${derivedIndex}`}
                type="radio"
                name={label}
                value={derivedIndex}
                checked={derivedIndex === checkedIndex}
                onChange={handleCheck}
                onFocus={onFocus}
              />
              <StarIconOutline
                component={StarScaleIcon}
                size={STAR_SIZE}
                _fieldColor={fieldColor}
                _isFilled={isFilled}
                _disabled={disabled}
              />
              <StarIconFilled
                component={StarScaleIcon}
                size={STAR_SIZE}
                _isFilled={isFilled}
                _fieldActiveColor={fieldActiveColor}
                _delay={delay}
              />
            </IconContainer>
          );
        })}
    </StarsContainer>
  );
}

const StarsContainer = styled.div.attrs<{
  _gap: number;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'star-scale-container'
  })
})`
  display: flex;
  font-size: 0;
  line-height: 0;
  gap: ${({ _gap }) => _gap}px;

  &:not(:last-child) {
    margin-right: 8px;
  }
`;

const StarIconOutline = styled(Icon).attrs<{
  _fieldColor: string;
  _disabled: boolean;
  _isFilled: boolean;
}>({
  className: createReadableClass({
    component: 'fields',
    element: 'star-icon-outline'
  })
})`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  & svg {
    transition: none;
    fill: none;
    stroke: ${({ _fieldColor, _disabled, _isFilled }) => {
      const alpha = (() => {
        if (_disabled) {
          return _isFilled ? 0 : 0.2;
        }

        return isDarkColor(_fieldColor) ? 0.3 : 0.4;
      })();

      return getColorWithAlpha(_fieldColor, alpha);
    }};
  }
`;

interface StarIconFilledProps {
  _isFilled: boolean;
  _fieldActiveColor: string;
  _delay: number;
}

const StarIconFilled = styled(Icon).attrs<StarIconFilledProps>({
  className: createReadableClass({
    component: 'fields',
    element: 'star-icon-filled'
  })
})<StarIconFilledProps>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition-property: transform, opacity;
  transition-timing-function: cubic-bezier(0.13, 0.15, 0.08, 1.5), linear;
  transform: ${({ _isFilled }) => (_isFilled ? 'scale(1)' : 'scale(0)')};
  opacity: ${({ _isFilled }) => (_isFilled ? 1 : 0)};
  transition-duration: ${({ _isFilled }) =>
    _isFilled ? '0.3s, 0.1s' : '0.6s, 0.2s'};
  transition-delay: ${({ _delay, _isFilled }) =>
    _isFilled ? `${_delay}s, calc(${_delay}s + 1ms)` : `${_delay}s, 0s`};

  & svg {
    transition: none;
    stroke: ${({ _fieldActiveColor }) => _fieldActiveColor};
    fill: ${({ _fieldActiveColor }) => _fieldActiveColor};
  }
`;

const IconContainer = styled.label.attrs<{
  _isFilled: boolean;
  _disabled: boolean;
}>(({ _isFilled }) => ({
  className: createReadableClass({
    component: 'fields',
    element: 'star-scale-icon-container',
    index: _isFilled && 'star-filled'
  })
}))`
  width: ${STAR_SIZE}px;
  height: ${STAR_SIZE}px;
  position: relative;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: transform 0.2s ease;

  ${({ _disabled }) =>
    _disabled &&
    css`
      pointer-events: none;
    `}

  &:hover {
    transform: scale(1.1);
  }
`;

const Input = styled.input.attrs({
  className: createReadableClass({
    component: 'fields',
    element: 'star-scale-input'
  })
})`
  position: absolute;
  cursor: pointer;
  margin: 0;
  opacity: 0;
  width: ${STAR_SIZE}px;
  height: ${STAR_SIZE}px;
`;
